import { createContext, useContext } from "react"

/**
 * Does this container space its children VERTICALLY?
 *
 * Set by the container that declared the stack, read by atoms that would
 * otherwise render inline. It exists because the two facts live on different
 * elements: the parent says `space-y-3`, the child decides `<span>` vs
 * `<div>`, and neither could see the other. Measured live 2026-08-24, that
 * blindness rendered an entire agent surface as one run-on line -- `space-y-*`
 * is a `margin-top` rule and margin-top does nothing to an inline box, so the
 * spacing was dropped without a warning anywhere.
 *
 * Scoped per container rather than carried on RenderContext: RenderContext is
 * whole-tree, and "my parent stacks me" is true of one level only. A nested
 * inline row inside a stacked column must still render inline.
 */
export const StackedChildrenContext = createContext(false)

export function useStacksChildren(): boolean {
  return useContext(StackedChildrenContext)
}

/**
 * The class names that mean "children are laid out as a vertical run".
 *
 * `space-y-*` is the direct case. `flex-col` and `grid` are the same failure
 * in a different costume: gap applies between flex/grid ITEMS, and a run of
 * inline children collapses into a single item, so the gap has nothing to act
 * between.
 *
 * `flex` WITHOUT `flex-col` is deliberately absent -- a row of inline text is
 * a legitimate thing to author, and promoting it would break it.
 */
export function declaresVerticalStack(className: string | undefined): boolean {
  if (!className) return false
  return /(^|\s)(space-y-\S+|flex-col|grid)(\s|$)/.test(className)
}
